import { useLoaderData, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";

import BrowserTimezoneAlert from "components/BrowserTimezoneAlert";
import EmptyStatePlaceholder from "components/EmptyStatePlaceholder";
import FilterSection from "components/FilterSection";
import LoadingStateProgressBar from "components/LoadingStateProgressBar";
import PageHeader from "components/PageHeader";
import SearchParamControlledAutocomplete from "components/SearchParamControlledAutocomplete";
import StickyTableCell from "components/StickyTableCell";
import SyncRunStatus from "components/SyncRunStatus";
import TableContainerWithScrollShadow from "components/TableContainerWithScrollShadow";

import { getCronDescriptionWithTzOffset } from "helpers/cron";
import { capitalizeFirstChar } from "helpers/string";
import { formatTimestamp, formatDuration } from "helpers/datetime";
import { getLoggedInUser } from "helpers/auth";
import buildRoute from "../components/buildRoute";
import { trackEvent, trackEngagementActivity } from "../configureMixpanel";
import { SYNC_RUN_STATUSES } from "constants";

function getUniqueValuesOfKeyFromObjectList(list, key) {
  return [...new Set(list.map((item) => item[key]).filter(Boolean))];
}

function ManagedSyncsIndexPage() {
  const { projectId } = useParams();
  const { isSuperUser } = useLoaderData();
  const [syncs, setSyncs] = useState([]);
  const [allVendorNames, setAllVendorNames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Some custom styling for header cells
  const firstHeaderCellStyles = {
    minWidth: 200,
    zIndex: 10,
    borderBottom: "none",
  };
  const headerCellStyles = {
    ...firstHeaderCellStyles,
    zIndex: 1,
    border: "none",
  };

  useEffect(() => {
    const fetchSyncsData = async () => {
      if (!projectId) {
        setError('Project ID is required to load syncs data.');
        setIsLoading(false);
        return;
      }

      const loadStartTime = Date.now();
      const urlSearchParams = new URLSearchParams(window.location.search);
      const vendorName = urlSearchParams.get("vendorName");

      setIsLoading(true);
      setError(null);
      try {
        // Fetch filtered syncs
        const queryParams = new URLSearchParams();
        if (vendorName) queryParams.set("vendor_name", vendorName);
        if (projectId) queryParams.set("project_id", projectId);

        const response = await fetch(`/syncs/api/syncs/?${queryParams.toString()}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const syncsData = await response.json();

        // Fetch unfiltered syncs for vendor names dropdown
        const unfilteredParams = new URLSearchParams();
        if (projectId) unfilteredParams.set("project_id", projectId);

        const unfilteredResponse = await fetch(`/syncs/api/syncs/?${unfilteredParams.toString()}`);
        if (!unfilteredResponse.ok) {
          throw new Error(`HTTP error! status: ${unfilteredResponse.status}`);
        }
        const unfilteredSyncs = await unfilteredResponse.json();

        const vendorNames = getUniqueValuesOfKeyFromObjectList(unfilteredSyncs, "vendor_name").sort();

        setSyncs(syncsData);
        setAllVendorNames(vendorNames);

        // Worst-observed status across the returned syncs: a schedule with
        // any failed run reads as "error", any in-flight run as
        // "next_run_pending", otherwise "enabled".
        let scheduleStatus = "enabled";
        if (syncsData.some((sync) => sync.latest_state === SYNC_RUN_STATUSES.FAILED)) {
          scheduleStatus = "error";
        } else if (syncsData.some((sync) => sync.latest_state === SYNC_RUN_STATUSES.RUNNING)) {
          scheduleStatus = "next_run_pending";
        }

        trackEvent("sync_schedule_checked", {
          sync_source: vendorName || "all",
          schedule_status: scheduleStatus,
          time_to_load_seconds: (Date.now() - loadStartTime) / 1000,
          num_items_in_schedule: syncsData.length,
        });
        trackEngagementActivity("sync_schedule_viewed");
      } catch (error) {
        console.error('Error fetching syncs data:', error);
        setError(error.message || 'Failed to load syncs data. Please try again later.');
        // "load_failed" (the page/API call failed) is distinct from "error"
        // above (the page loaded fine but a sync run itself failed) - these
        // are different failure modes and conflating them would make this
        // event misleadingly suggest sync failures when the real cause was
        // our API being unreachable.
        trackEvent("sync_schedule_checked", {
          sync_source: vendorName || "all",
          schedule_status: "load_failed",
          time_to_load_seconds: (Date.now() - loadStartTime) / 1000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSyncsData();
  }, [projectId]);

  return (
    <>
      <PageHeader
        title="Managed Syncs"
        subtitle="Syncs managed and created by CTA for this project"
      />
      <Box
        component="section"
        sx={{
          pt: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <BrowserTimezoneAlert sx={{ mb: 2 }} />

        <FilterSection sx={{ mb: 1, zIndex: 2 }}>
          <SearchParamControlledAutocomplete
            searchParam="vendorName"
            label="Source"
            options={allVendorNames ?? []}
          />
        </FilterSection>

        <LoadingStateProgressBar />

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ color: 'error.main', mb: 2 }}>
              Error loading syncs data
            </Box>
            <Box sx={{ color: 'text.secondary' }}>
              {error}
            </Box>
          </Box>
        ) : syncs.length ? (
          <TableContainerWithScrollShadow sx={{ flex: 1 }}>
            <Table sx={{ minWidth: 650 }} stickyHeader aria-label="Syncs table">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={firstHeaderCellStyles}>
                    Sync Name
                  </StickyTableCell>
                  {isSuperUser && <TableCell sx={headerCellStyles}>Source</TableCell>}
                  <TableCell sx={headerCellStyles}>Schedule</TableCell>
                  <TableCell sx={headerCellStyles}>Last run status</TableCell>
                  <TableCell sx={headerCellStyles}>Last run duration</TableCell>
                  <TableCell sx={headerCellStyles}>Last run end</TableCell>
                  <TableCell sx={headerCellStyles}>Next run</TableCell>
                  {isSuperUser && <TableCell sx={headerCellStyles}>Sync Type</TableCell>}
                  {isSuperUser && <TableCell sx={headerCellStyles}>Sync ID</TableCell>}
                  {isSuperUser && <TableCell sx={headerCellStyles}>Tags</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {syncs.map((sync) => {
                  const tags = sync.additional_info?.dag?.tags;
                  return (
                    <TableRow key={sync.sync_id}>
                      <StickyTableCell>
                        {sync.sync_name ?? "-"}
                      </StickyTableCell>
                      {isSuperUser && <TableCell>{sync.vendor_name ?? "-"}</TableCell>}
                      <TableCell>
                        {sync.schedule
                          ? getCronDescriptionWithTzOffset(sync.schedule)
                          : "No schedule provided"}
                      </TableCell>
                      <TableCell>
                        <SyncRunStatus status={sync.latest_state} />
                      </TableCell>
                      <TableCell>
                        {formatDuration(sync.latest_execution_start_time, sync.latest_execution_end_time) || "-"}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(sync.latest_execution_end_time) ??
                          "-"}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(sync.additional_info?.dag?.next_dagrun_data_interval_end) ??
                          "-"}
                      </TableCell>
                      {isSuperUser && <TableCell>{sync.sync_type ? capitalizeFirstChar(sync.sync_type) : "-"}</TableCell>}
                      {isSuperUser && <TableCell>{sync.sync_id ?? "-"}</TableCell>}
                      {isSuperUser && <TableCell>
                        {Array.isArray(tags) && tags.length > 0
                          ? tags.map((tagObj) => tagObj.name).join(", ")
                          : "-"}
                      </TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainerWithScrollShadow>
        ) : (
          <EmptyStatePlaceholder
            title="No syncs to show"
            subtitle="Try adjusting your filters above. Otherwise, CTA-managed syncs will appear here after they are set up."
          />
        )}
      </Box>
    </>
  );
}

const routeArgs = {
  element: <ManagedSyncsIndexPage />,
  loader: async () => {
    const loggedInUser = await getLoggedInUser();
    const isSuperUser = loggedInUser?.is_superuser || false;

    return { isSuperUser };
  },
};

export default buildRoute(routeArgs);
